import type { FC, FormEvent, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import {
    ArrowRight,
    ArrowUpRight,
    Check,
    ChevronDown,
    CircleAlert,
    CircleCheck,
    Download,
    LoaderCircle,
    Minus,
    Plus
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClasses } from '@/components/generals/button-link'
import { SectionHeading } from '@/components/generals/section-heading'
import { TipSchool } from '@/components/landing/tip-school'

import {
    CONTACT_EMAIL,
    DOWNLOADS,
    GROEPEN,
    MAILTO_VRAAG,
    ROLES,
    WENSEN,
    external
} from '@/constants/leesritme'

type Tab = 'pilot' | 'offerte'
type Status = 'idle' | 'sending' | 'success' | 'error'

interface PilotState {
    rol: string
    naam: string
    email: string
    school: string
    plaats: string
    aantalLeerlingen: string
    aantalKinderen: string
    groepen: Array<string>
    doel: string
    geslaagdAls: string
    wensen: Array<string>
    website: string
}

interface OfferteState {
    naam: string
    functie: string
    email: string
    school: string
    plaats: string
    aantalLeerlingen: string
    isBestuur: boolean
    aantalScholen: string
    bericht: string
    website: string
}

interface FieldProps {
    id: string
    label: string
    optional?: boolean
    className?: string
    children: ReactNode
}

interface ChipGroupProps {
    name: string
    legend: string
    options: Array<string>
    selected: Array<string>
    onToggle: (value: string) => void
    format?: (value: string) => string
    withIcon?: boolean
}

interface SuccessPanelProps {
    title: string
    children: ReactNode
    onReset: () => void
    resetLabel: string
}

const INITIAL_PILOT: PilotState = {
    rol: 'Leerkracht',
    naam: '',
    email: '',
    school: '',
    plaats: '',
    aantalLeerlingen: '25',
    aantalKinderen: '1',
    groepen: [],
    doel: '',
    geslaagdAls: '',
    wensen: [],
    website: ''
}

const INITIAL_OFFERTE: OfferteState = {
    naam: '',
    functie: '',
    email: '',
    school: '',
    plaats: '',
    aantalLeerlingen: '',
    isBestuur: false,
    aantalScholen: '3',
    bericht: '',
    website: ''
}

const INPUT_CLASS = 'block w-full rounded-xl border border-input bg-white px-4 py-3 text-base text-ink transition placeholder:text-muted-foreground/70 focus:border-leaf focus:ring-4 focus:ring-leaf/15 focus:outline-none'
const FALLBACK_ERROR = `Versturen lukte niet. Probeer het opnieuw, of mail naar ${CONTACT_EMAIL}.`

const toggleValue = (list: Array<string>, value: string): Array<string> => {
    return list.includes(value) ? list.filter(item => item !== value) : [...list, value]
}

const stepValue = (value: string, delta: number, min: number, max: number): string => {
    const current = Number.parseInt(value, 10)
    const base = Number.isFinite(current) ? current : min

    return String(Math.min(max, Math.max(min, base + delta)))
}

const firstName = (naam: string): string => naam.trim().split(/\s+/)[0] ?? ''

const postSubmission = async (payload: Record<string, unknown>): Promise<void> => {
    let response: Response

    try {
        response = await fetch('/api/aanmelden', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        })
    }

    catch {
        throw new Error(FALLBACK_ERROR)
    }

    if (response.ok) return

    const result: unknown = await response.json().catch(() => null)
    const message = typeof result === 'object' && result !== null && 'message' in result && typeof result.message === 'string'
        ? result.message
        : FALLBACK_ERROR

    throw new Error(message)
}

const Field: FC<FieldProps> = ({ id, label, optional = false, className, children }): ReactNode => {
    return (
        <div className={className}>
            <label htmlFor={id} className='flex items-baseline justify-between gap-2 text-sm font-bold text-ink'>
                {label}

                {optional && (
                    <span className='text-xs font-normal text-muted-foreground'>
                        mag leeg
                    </span>
                )}
            </label>

            <div className='mt-1.5'>
                {children}
            </div>
        </div>
    )
}

const ChipGroup: FC<ChipGroupProps> = ({
    name,
    legend,
    options,
    selected,
    onToggle,
    format = value => value,
    withIcon = false
}): ReactNode => {
    return (
        <fieldset>
            <legend className='text-sm font-bold text-ink'>
                {legend}
            </legend>

            <div className='mt-2 flex flex-wrap gap-2'>
                {options.map((option, index) => {
                    const checked = selected.includes(option)

                    return (
                        <label
                            key={option}
                            htmlFor={`${name}-${index}`}
                            className={cn(
                                'inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold transition select-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-leaf has-[:focus-visible]:ring-offset-1',
                                checked ? 'border-leaf bg-leaf text-white' : 'border-input bg-white text-ink hover:border-ink/30'
                            )}
                        >
                            <input
                                id={`${name}-${index}`}
                                type='checkbox'
                                name={name}
                                value={option}
                                checked={checked}
                                onChange={() => onToggle(option)}
                                className='sr-only'
                            />

                            {withIcon && (checked ? <Check className='size-3.5' aria-hidden='true' /> : <Plus className='size-3.5' aria-hidden='true' />)}
                            {format(option)}
                        </label>
                    )
                })}
            </div>
        </fieldset>
    )
}

const SuccessPanel: FC<SuccessPanelProps> = ({ title, children, onReset, resetLabel }): ReactNode => {
    const headingRef = useRef<HTMLHeadingElement>(null)

    useEffect(() => {
        headingRef.current?.focus()
    }, [])

    return (
        <div role='status' className='flex flex-col items-start py-6 animate-in fade-in slide-in-from-bottom-2 duration-300'>
            <span className='grid size-14 place-items-center rounded-2xl bg-leaf-soft text-leaf'>
                <CircleCheck className='size-8' aria-hidden='true' />
            </span>

            <h3 ref={headingRef} tabIndex={-1} className='mt-5 font-heading text-2xl leading-tight font-semibold text-ink focus:outline-none sm:text-3xl'>
                {title}
            </h3>

            <div className='mt-3 text-[1.0625rem] leading-relaxed text-muted-foreground'>
                {children}
            </div>

            <div className='mt-7 flex flex-wrap gap-3'>
                <a href={external('/portaal')} className={buttonClasses('primary', 'md')}>
                    Naar het portaal
                    <ArrowUpRight className='size-4' aria-hidden='true' />
                </a>

                <button type='button' onClick={onReset} className={buttonClasses('outline', 'md')}>
                    {resetLabel}
                </button>
            </div>
        </div>
    )
}

const ErrorMessage: FC<{ message: string }> = ({ message }): ReactNode => {
    if (!message) return null

    return (
        <p role='alert' className='flex items-start gap-2 rounded-xl bg-coral-soft px-4 py-3 text-sm text-coral-deep'>
            <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
            {message}
        </p>
    )
}

const Honeypot: FC<{ value: string, onChange: (value: string) => void }> = ({ value, onChange }): ReactNode => {
    return (
        <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
            <label>
                Website
                <input type='text' tabIndex={-1} autoComplete='off' value={value} onChange={event => onChange(event.target.value)} />
            </label>
        </div>
    )
}

export const SignupSection: FC = (): ReactNode => {
    const [tab, setTab] = useState<Tab>('pilot')
    const [pilot, setPilot] = useState<PilotState>(INITIAL_PILOT)
    const [pilotStatus, setPilotStatus] = useState<Status>('idle')
    const [pilotError, setPilotError] = useState('')
    const [offerte, setOfferte] = useState<OfferteState>(INITIAL_OFFERTE)
    const [offerteStatus, setOfferteStatus] = useState<Status>('idle')
    const [offerteError, setOfferteError] = useState('')
    const [tipOpen, setTipOpen] = useState(false)

    const isOuder = pilot.rol === 'Ouder'

    useEffect(() => {
        const sync = (): void => {
            if (window.location.hash === '#offerte') setTab('offerte')
        }

        const onClick = (event: MouseEvent): void => {
            const target = event.target instanceof Element ? event.target.closest('a') : null
            const href = target?.getAttribute('href') ?? ''

            if (href.endsWith('#offerte')) setTab('offerte')
            else if (href.endsWith('#aanmelden')) setTab('pilot')
        }

        sync()
        window.addEventListener('hashchange', sync)
        document.addEventListener('click', onClick)

        return () => {
            window.removeEventListener('hashchange', sync)
            document.removeEventListener('click', onClick)
        }
    }, [])

    const updatePilot = <K extends keyof PilotState>(key: K, value: PilotState[K]): void => {
        setPilot(current => ({ ...current, [key]: value }))
    }

    const updateOfferte = <K extends keyof OfferteState>(key: K, value: OfferteState[K]): void => {
        setOfferte(current => ({ ...current, [key]: value }))
    }

    const chooseRole = (rol: string): void => {
        updatePilot('rol', rol)

        if (rol === 'Ouder') setTipOpen(true)
    }

    const submitPilot = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (pilotStatus === 'sending') return

        setPilotStatus('sending')
        setPilotError('')

        try {
            await postSubmission({
                type: 'pilot',
                rol: pilot.rol,
                naam: pilot.naam,
                email: pilot.email,
                school: pilot.school,
                plaats: pilot.plaats,
                aantalLeerlingen: isOuder ? null : pilot.aantalLeerlingen,
                aantalKinderen: isOuder ? pilot.aantalKinderen : null,
                groepen: pilot.groepen,
                doel: pilot.doel,
                geslaagdAls: pilot.geslaagdAls,
                wensen: pilot.wensen,
                website: pilot.website
            })

            setPilotStatus('success')
        }

        catch (error) {
            setPilotStatus('error')
            setPilotError(error instanceof Error ? error.message : FALLBACK_ERROR)
        }
    }

    const submitOfferte = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (offerteStatus === 'sending') return

        setOfferteStatus('sending')
        setOfferteError('')

        try {
            await postSubmission({
                type: 'offerte',
                naam: offerte.naam,
                functie: offerte.functie,
                email: offerte.email,
                school: offerte.school,
                plaats: offerte.plaats,
                aantalLeerlingen: offerte.aantalLeerlingen,
                isBestuur: offerte.isBestuur,
                aantalScholen: offerte.isBestuur ? offerte.aantalScholen : null,
                bericht: offerte.bericht,
                website: offerte.website
            })

            setOfferteStatus('success')
        }

        catch (error) {
            setOfferteStatus('error')
            setOfferteError(error instanceof Error ? error.message : FALLBACK_ERROR)
        }
    }

    const resetPilot = (): void => {
        setPilot(INITIAL_PILOT)
        setPilotStatus('idle')
        setPilotError('')
    }

    const resetOfferte = (): void => {
        setOfferte(INITIAL_OFFERTE)
        setOfferteStatus('idle')
        setOfferteError('')
    }

    return (
        <section id='aanmelden' className='relative bg-sand/70 py-20 sm:py-28'>
            <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16'>
                <div>
                    <SectionHeading
                        eyebrow='Aanmelden'
                        title={(
                            <>
                                Start de <span className='font-medium text-leaf italic'>gratis pilot.</span>
                            </>
                        )}
                        intro='Volledige toegang voor je klas, gratis en zonder verplichting. Ben je ouder? Dan kan je kind ook thuis meedoen.'
                    />

                    <ol className='mt-10 grid gap-5'>
                        {[
                            { title: "Meld je aan", text: "Vul het formulier in. Dat kost je een minuut." },
                            { title: "Ontvang je pilotcode", text: "We mailen je code, zodat je je klas kunt klaarzetten." },
                            { title: "Start met je klas", text: "Klas aanmaken, klassenlijst plakken en inlogkaartjes printen: in vijf minuten klaar." }
                        ].map((step, index) => (
                            <li key={step.title} className='flex gap-4'>
                                <span className='grid size-10 shrink-0 place-items-center rounded-full bg-ink font-heading text-lg font-semibold text-paper'>
                                    {index + 1}
                                </span>

                                <span>
                                    <span className='block font-bold text-ink'>
                                        {step.title}
                                    </span>

                                    <span className='block text-[0.9375rem] leading-relaxed text-muted-foreground'>
                                        {step.text}
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ol>

                    <div className='mt-10 rounded-[1.5rem] border border-ink/10 bg-card p-6'>
                        <p className='font-heading text-lg font-semibold text-ink'>
                            Liever eerst rondkijken?
                        </p>

                        <ul className='mt-3 grid gap-2'>
                            <li>
                                <a href={DOWNLOADS.startgids} className='inline-flex items-center gap-2 text-[0.9375rem] font-semibold text-leaf underline decoration-leaf/30 underline-offset-4 hover:decoration-leaf'>
                                    <Download className='size-4' aria-hidden='true' />
                                    Startgids voor leerkrachten (pdf)
                                </a>
                            </li>

                            <li>
                                <a href={DOWNLOADS.proefweek} className='inline-flex items-center gap-2 text-[0.9375rem] font-semibold text-leaf underline decoration-leaf/30 underline-offset-4 hover:decoration-leaf'>
                                    <Download className='size-4' aria-hidden='true' />
                                    Proefweek De oceaan, groep 6 (pdf)
                                </a>
                            </li>

                            <li>
                                <a href={external('/demo')} className='inline-flex items-center gap-2 text-[0.9375rem] font-semibold text-leaf underline decoration-leaf/30 underline-offset-4 hover:decoration-leaf'>
                                    <ArrowUpRight className='size-4' aria-hidden='true' />
                                    Probeer de leerlingdemo
                                </a>
                            </li>
                        </ul>

                        <p className='mt-5 border-t border-ink/10 pt-4 text-sm text-muted-foreground'>
                            Heb je al een code of account?{' '}
                            <a href={external('/portaal')} className='font-bold text-ink underline decoration-ink/20 underline-offset-4 hover:decoration-ink'>
                                Naar het portaal
                            </a>
                        </p>
                    </div>
                </div>

                <div className='grid content-start gap-6'>
                    <div className='relative rounded-[2rem] border border-ink/10 bg-card p-5 shadow-lift sm:p-8'>
                        <span id='offerte' className='absolute top-0 left-0' aria-hidden='true' />

                        <div role='tablist' aria-label='Kies een formulier' className='grid grid-cols-2 gap-1 rounded-full bg-sand p-1'>
                            {([['pilot', 'Gratis pilot'], ['offerte', 'Offerte aanvragen']] as Array<[Tab, string]>).map(([value, label]) => (
                                <button
                                    key={value}
                                    type='button'
                                    role='tab'
                                    id={`formulier-tab-${value}`}
                                    aria-selected={tab === value}
                                    aria-controls={`formulier-${value}`}
                                    onClick={() => setTab(value)}
                                    className={cn(
                                        'rounded-full px-3 py-2.5 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf sm:text-[0.9375rem]',
                                        tab === value ? 'bg-ink text-paper shadow-soft' : 'text-ink/70 hover:text-ink'
                                    )}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        <div id='formulier-pilot' role='tabpanel' aria-labelledby='formulier-tab-pilot' hidden={tab !== 'pilot'} className='mt-6'>
                            {pilotStatus === 'success'
                                ? (
                                    <SuccessPanel
                                        title={`Bedankt${firstName(pilot.naam) ? `, ${firstName(pilot.naam)}` : ''}! Je aanmelding is binnen.`}
                                        onReset={resetPilot}
                                        resetLabel='Nog iemand aanmelden'
                                    >
                                        {isOuder
                                            ? (
                                                <p>
                                                    We mailen je een code naar <strong className='text-ink'>{pilot.email}</strong>. Daarmee maak je een account en zet je je kind klaar om thuis te lezen.
                                                </p>
                                            )
                                            : (
                                                <p>
                                                    We mailen je persoonlijke pilotcode naar <strong className='text-ink'>{pilot.email}</strong>, zodat je met je klas kunt beginnen.
                                                </p>
                                            )}
                                    </SuccessPanel>
                                )
                                : (
                                    <form onSubmit={submitPilot} className='relative grid gap-7'>
                                        <div className='grid gap-5'>
                                            <h3 className='font-heading text-xl font-semibold text-ink'>
                                                1. Wie ben je?
                                            </h3>

                                            <fieldset>
                                                <legend className='text-sm font-bold text-ink'>
                                                    Ik ben
                                                </legend>

                                                <div className='mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4'>
                                                    {ROLES.map(role => {
                                                        const Icon = role.icon
                                                        const checked = pilot.rol === role.value

                                                        return (
                                                            <label
                                                                key={role.value}
                                                                className={cn(
                                                                    'flex cursor-pointer flex-col items-center gap-1.5 rounded-2xl border px-2 py-3 text-center text-sm font-bold transition select-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-leaf',
                                                                    checked ? 'border-leaf bg-leaf-soft text-leaf shadow-[inset_0_0_0_1px_var(--leaf)]' : 'border-input bg-white text-ink hover:border-ink/30'
                                                                )}
                                                            >
                                                                <input
                                                                    type='radio'
                                                                    name='rol'
                                                                    value={role.value}
                                                                    checked={checked}
                                                                    onChange={() => chooseRole(role.value)}
                                                                    className='sr-only'
                                                                />

                                                                <Icon className='size-5' aria-hidden='true' />
                                                                {role.label}
                                                            </label>
                                                        )
                                                    })}
                                                </div>
                                            </fieldset>

                                            <div className='grid gap-4 sm:grid-cols-2'>
                                                <Field id='pilot-naam' label='Naam'>
                                                    <input
                                                        id='pilot-naam'
                                                        type='text'
                                                        required
                                                        autoComplete='name'
                                                        maxLength={120}
                                                        value={pilot.naam}
                                                        onChange={event => updatePilot('naam', event.target.value)}
                                                        className={INPUT_CLASS}
                                                    />
                                                </Field>

                                                <Field id='pilot-email' label='E-mail'>
                                                    <input
                                                        id='pilot-email'
                                                        type='email'
                                                        required
                                                        autoComplete='email'
                                                        maxLength={200}
                                                        value={pilot.email}
                                                        onChange={event => updatePilot('email', event.target.value)}
                                                        className={INPUT_CLASS}
                                                    />
                                                </Field>

                                                <Field id='pilot-school' label={isOuder ? 'School van je kind' : 'School'} optional={isOuder}>
                                                    <input
                                                        id='pilot-school'
                                                        type='text'
                                                        required={!isOuder}
                                                        autoComplete='organization'
                                                        maxLength={160}
                                                        value={pilot.school}
                                                        onChange={event => updatePilot('school', event.target.value)}
                                                        className={INPUT_CLASS}
                                                    />
                                                </Field>

                                                <Field id='pilot-plaats' label='Plaats' optional>
                                                    <input
                                                        id='pilot-plaats'
                                                        type='text'
                                                        autoComplete='address-level2'
                                                        maxLength={120}
                                                        value={pilot.plaats}
                                                        onChange={event => updatePilot('plaats', event.target.value)}
                                                        className={INPUT_CLASS}
                                                    />
                                                </Field>
                                            </div>
                                        </div>

                                        <div className='grid gap-5 border-t border-ink/10 pt-7'>
                                            <h3 className='font-heading text-xl font-semibold text-ink'>
                                                2. Wie doen er mee?
                                            </h3>

                                            {isOuder
                                                ? (
                                                    <Field id='pilot-kinderen' label='Hoeveel kinderen doen mee?' className='max-w-xs'>
                                                        <select
                                                            id='pilot-kinderen'
                                                            value={pilot.aantalKinderen}
                                                            onChange={event => updatePilot('aantalKinderen', event.target.value)}
                                                            className={INPUT_CLASS}
                                                        >
                                                            {['1', '2', '3', '4', '5'].map(option => (
                                                                <option key={option} value={option}>
                                                                    {option === '1' ? '1 kind' : `${option} kinderen`}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </Field>
                                                )
                                                : (
                                                    <Field id='pilot-leerlingen' label='Hoeveel leerlingen doen er mee?'>
                                                        <div className='flex items-center gap-2'>
                                                            <button
                                                                type='button'
                                                                onClick={() => updatePilot('aantalLeerlingen', stepValue(pilot.aantalLeerlingen, -5, 1, 5000))}
                                                                aria-label='5 leerlingen minder'
                                                                className='grid size-12 shrink-0 place-items-center rounded-xl border border-input bg-white text-ink transition hover:border-ink/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf'
                                                            >
                                                                <Minus className='size-4' aria-hidden='true' />
                                                            </button>

                                                            <input
                                                                id='pilot-leerlingen'
                                                                type='number'
                                                                inputMode='numeric'
                                                                min={1}
                                                                max={5000}
                                                                required
                                                                value={pilot.aantalLeerlingen}
                                                                onChange={event => updatePilot('aantalLeerlingen', event.target.value)}
                                                                className={cn(INPUT_CLASS, 'w-28 text-center font-bold tabular-nums')}
                                                            />

                                                            <button
                                                                type='button'
                                                                onClick={() => updatePilot('aantalLeerlingen', stepValue(pilot.aantalLeerlingen, 5, 1, 5000))}
                                                                aria-label='5 leerlingen meer'
                                                                className='grid size-12 shrink-0 place-items-center rounded-xl border border-input bg-white text-ink transition hover:border-ink/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf'
                                                            >
                                                                <Plus className='size-4' aria-hidden='true' />
                                                            </button>
                                                        </div>
                                                    </Field>
                                                )}

                                            <ChipGroup
                                                name='pilot-groepen'
                                                legend={isOuder ? 'In welke groep zit je kind?' : 'Welke groepen?'}
                                                options={GROEPEN}
                                                selected={pilot.groepen}
                                                onToggle={value => updatePilot('groepen', toggleValue(pilot.groepen, value))}
                                                format={value => `Groep ${value}`}
                                            />
                                        </div>

                                        <details className='group rounded-2xl border border-ink/10 bg-paper'>
                                            <summary className='flex cursor-pointer list-none items-center justify-between gap-4 rounded-2xl px-5 py-4 font-bold text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf [&::-webkit-details-marker]:hidden'>
                                                <span>
                                                    Wil je meer vertellen?
                                                    <span className='ml-2 text-sm font-normal text-muted-foreground'>mag leeg</span>
                                                </span>

                                                <ChevronDown className='size-5 shrink-0 transition-transform group-open:rotate-180' aria-hidden='true' />
                                            </summary>

                                            <div className='grid gap-5 px-5 pb-5'>
                                                <Field id='pilot-doel' label='Wat hoop je uit de pilot te halen?' optional>
                                                    <textarea
                                                        id='pilot-doel'
                                                        rows={3}
                                                        maxLength={1500}
                                                        value={pilot.doel}
                                                        onChange={event => updatePilot('doel', event.target.value)}
                                                        className={cn(INPUT_CLASS, 'resize-y')}
                                                    />
                                                </Field>

                                                <Field id='pilot-geslaagd' label='Wanneer is de pilot voor jou geslaagd?' optional>
                                                    <textarea
                                                        id='pilot-geslaagd'
                                                        rows={3}
                                                        maxLength={1500}
                                                        value={pilot.geslaagdAls}
                                                        onChange={event => updatePilot('geslaagdAls', event.target.value)}
                                                        className={cn(INPUT_CLASS, 'resize-y')}
                                                    />
                                                </Field>

                                                <ChipGroup
                                                    name='pilot-wensen'
                                                    legend='Wat zou je graag terugzien in de app?'
                                                    options={WENSEN}
                                                    selected={pilot.wensen}
                                                    onToggle={value => updatePilot('wensen', toggleValue(pilot.wensen, value))}
                                                    withIcon
                                                />
                                            </div>
                                        </details>

                                        <Honeypot value={pilot.website} onChange={value => updatePilot('website', value)} />

                                        <ErrorMessage message={pilotError} />

                                        <div>
                                            <button type='submit' disabled={pilotStatus === 'sending'} className={buttonClasses('primary', 'lg', 'w-full')}>
                                                {pilotStatus === 'sending'
                                                    ? <LoaderCircle className='size-5 animate-spin' aria-hidden='true' />
                                                    : null}
                                                {pilotStatus === 'sending' ? 'Bezig met versturen' : isOuder ? 'Meld mijn kind aan' : 'Start gratis met je klas'}
                                                {pilotStatus !== 'sending' && <ArrowRight className='size-4' aria-hidden='true' />}
                                            </button>

                                            <p className='mt-3 text-center text-sm text-muted-foreground'>
                                                Gratis en zonder verplichtingen. We gebruiken je gegevens alleen voor de pilot.
                                            </p>
                                        </div>
                                    </form>
                                )}
                        </div>

                        <div id='formulier-offerte' role='tabpanel' aria-labelledby='formulier-tab-offerte' hidden={tab !== 'offerte'} className='mt-6'>
                            {offerteStatus === 'success'
                                ? (
                                    <SuccessPanel
                                        title='Dank je wel! Je aanvraag is binnen.'
                                        onReset={resetOfferte}
                                        resetLabel='Nieuwe aanvraag'
                                    >
                                        <p>
                                            Roman neemt persoonlijk contact met je op over een offerte op maat voor <strong className='text-ink'>{offerte.school}</strong>.
                                        </p>
                                    </SuccessPanel>
                                )
                                : (
                                    <form onSubmit={submitOfferte} className='relative grid gap-6'>
                                        <div>
                                            <h3 className='font-heading text-xl font-semibold text-ink'>
                                                Vraag een offerte aan
                                            </h3>

                                            <p className='mt-1.5 text-[0.9375rem] leading-relaxed text-muted-foreground'>
                                                Je krijgt een offerte op maat van je school, zonder verplichting. Roman neemt persoonlijk contact met je op.
                                            </p>
                                        </div>

                                        <div className='grid gap-4 sm:grid-cols-2'>
                                            <Field id='offerte-naam' label='Naam'>
                                                <input
                                                    id='offerte-naam'
                                                    type='text'
                                                    required
                                                    autoComplete='name'
                                                    maxLength={120}
                                                    value={offerte.naam}
                                                    onChange={event => updateOfferte('naam', event.target.value)}
                                                    className={INPUT_CLASS}
                                                />
                                            </Field>

                                            <Field id='offerte-functie' label='Functie' optional>
                                                <input
                                                    id='offerte-functie'
                                                    type='text'
                                                    autoComplete='organization-title'
                                                    maxLength={120}
                                                    value={offerte.functie}
                                                    onChange={event => updateOfferte('functie', event.target.value)}
                                                    className={INPUT_CLASS}
                                                />
                                            </Field>

                                            <Field id='offerte-email' label='E-mail'>
                                                <input
                                                    id='offerte-email'
                                                    type='email'
                                                    required
                                                    autoComplete='email'
                                                    maxLength={200}
                                                    value={offerte.email}
                                                    onChange={event => updateOfferte('email', event.target.value)}
                                                    className={INPUT_CLASS}
                                                />
                                            </Field>

                                            <Field id='offerte-school' label='School of bestuur'>
                                                <input
                                                    id='offerte-school'
                                                    type='text'
                                                    required
                                                    autoComplete='organization'
                                                    maxLength={160}
                                                    value={offerte.school}
                                                    onChange={event => updateOfferte('school', event.target.value)}
                                                    className={INPUT_CLASS}
                                                />
                                            </Field>

                                            <Field id='offerte-plaats' label='Plaats' optional>
                                                <input
                                                    id='offerte-plaats'
                                                    type='text'
                                                    autoComplete='address-level2'
                                                    maxLength={120}
                                                    value={offerte.plaats}
                                                    onChange={event => updateOfferte('plaats', event.target.value)}
                                                    className={INPUT_CLASS}
                                                />
                                            </Field>

                                            <Field id='offerte-leerlingen' label='Aantal leerlingen (groep 3 t/m 8)'>
                                                <input
                                                    id='offerte-leerlingen'
                                                    type='number'
                                                    inputMode='numeric'
                                                    required
                                                    min={1}
                                                    max={1000000}
                                                    value={offerte.aantalLeerlingen}
                                                    onChange={event => updateOfferte('aantalLeerlingen', event.target.value)}
                                                    className={cn(INPUT_CLASS, 'tabular-nums')}
                                                />
                                            </Field>
                                        </div>

                                        <div className='rounded-2xl border border-ink/10 bg-paper p-4'>
                                            <label className='flex cursor-pointer items-center gap-3 text-[0.9375rem] font-semibold text-ink'>
                                                <input
                                                    type='checkbox'
                                                    checked={offerte.isBestuur}
                                                    onChange={event => updateOfferte('isBestuur', event.target.checked)}
                                                    className='size-5 shrink-0 cursor-pointer rounded accent-leaf'
                                                />
                                                Bestuur met drie of meer scholen
                                            </label>

                                            {offerte.isBestuur && (
                                                <Field id='offerte-scholen' label='Met hoeveel scholen?' className='mt-4 max-w-[12rem]'>
                                                    <input
                                                        id='offerte-scholen'
                                                        type='number'
                                                        inputMode='numeric'
                                                        required
                                                        min={3}
                                                        max={1000}
                                                        value={offerte.aantalScholen}
                                                        onChange={event => updateOfferte('aantalScholen', event.target.value)}
                                                        className={cn(INPUT_CLASS, 'tabular-nums')}
                                                    />
                                                </Field>
                                            )}
                                        </div>

                                        <Field id='offerte-bericht' label='Vraag of opmerking' optional>
                                            <textarea
                                                id='offerte-bericht'
                                                rows={4}
                                                maxLength={3000}
                                                value={offerte.bericht}
                                                onChange={event => updateOfferte('bericht', event.target.value)}
                                                className={cn(INPUT_CLASS, 'resize-y')}
                                            />
                                        </Field>

                                        <Honeypot value={offerte.website} onChange={value => updateOfferte('website', value)} />

                                        <ErrorMessage message={offerteError} />

                                        <div>
                                            <button type='submit' disabled={offerteStatus === 'sending'} className={buttonClasses('primary', 'lg', 'w-full')}>
                                                {offerteStatus === 'sending'
                                                    ? <LoaderCircle className='size-5 animate-spin' aria-hidden='true' />
                                                    : null}
                                                {offerteStatus === 'sending' ? 'Bezig met versturen' : 'Verstuur aanvraag'}
                                                {offerteStatus !== 'sending' && <ArrowRight className='size-4' aria-hidden='true' />}
                                            </button>

                                            <p className='mt-3 text-center text-sm text-muted-foreground'>
                                                Liever eerst praten?{' '}
                                                <a href={MAILTO_VRAAG} className='font-semibold text-ink underline decoration-ink/20 underline-offset-4 hover:decoration-ink'>
                                                    Mail Roman
                                                </a>
                                            </p>
                                        </div>
                                    </form>
                                )}
                        </div>
                    </div>

                    {tab === 'pilot' && (
                        <TipSchool open={tipOpen} onOpenChange={setTipOpen} />
                    )}
                </div>
            </div>
        </section>
    )
}
